import { _ as _applyDecoratedDescriptor, a as _initializerDefineProperty } from '../_rollupPluginBabelHelpers-3b0ba74f.js';
import Component from '@glimmer/component';
import { getOwner } from '@ember/application';
import { tracked } from '@glimmer/tracking';
import { action, set } from '@ember/object';
import { inject } from '@ember/service';
import { task, timeout } from 'ember-concurrency';
import { next } from '@ember/runloop';
import { guidFor } from '@ember/object/internals';
import { createPopper } from '@popperjs/core';
import { paginatorPageLists } from 'beebee-base/lib/utility';
import { precompileTemplate } from '@ember/template-compilation';
import { setComponentTemplate } from '@ember/component';

var TEMPLATE = precompileTemplate("{{!-- UI nuova: a real combobox (the field is the search input) — see «UI nuova combobox» in select-association.js.\n     The field is ALWAYS the input (phone or not is decided when the list opens, never at render: a render-time\n     choice kept a button — no typing — after the window grew). Classic: the BasicDropdown branch in {{else}}. --}}\n{{#if this.isNuova}}\n  <div\n    class=\'nv-cb {{if this.cbOpen \"is-open\"}}\'\n    data-test-nv-cb-field\n    ...attributes\n    {{did-insert this.cbRegisterRoot}}\n  >\n    <FaIcon @icon=\'magnifying-glass\' class=\'nv-cb-ic\' aria-hidden=\'true\' />\n    <input\n      type=\'text\'\n      class=\'nv-cb-input\'\n      role=\'combobox\'\n      autocomplete=\'off\'\n      aria-autocomplete=\'list\'\n      aria-expanded={{if this.cbOpen \'true\' \'false\'}}\n      aria-controls={{this.cbListId}}\n      aria-activedescendant={{this.cbActiveId}}\n      aria-label={{this.labelSearchInput}}\n      placeholder={{this.labelSearchInput}}\n      value={{this.searchValue}}\n      data-test-nv-cb-input\n      {{on \'focus\' this.cbOpenList}}\n      {{on \'click\' this.cbOpenList}}\n      {{on \'input\' this.cbInput}}\n      {{on \'keydown\' this.cbKeydown}}\n      {{on \'blur\' this.cbBlur}}\n    />\n    {{#if this.searchItem.isRunning}}\n      <span class=\'nv-cb-spin\' aria-hidden=\'true\'><BbSpinner /></span>\n    {{/if}}\n  </div>\n\n  {{#if this.cbOpen}}\n    {{#in-element this.destinationElement insertBefore=null}}\n      <div\n        class=\'nv-cb-layer {{if this.cbPhone \"nv-cb-layer--phone\"}}\'\n        data-nv-layer\n        data-nv-cb={{this.cbListId}}\n        aria-hidden={{if this.cbConfirmOpen \'true\'}}\n        data-test-nv-cb-layer\n      >\n        {{! template-lint-disable no-invalid-interactive }}\n        <div\n          class=\'nv-cb-pop {{if this.cbPhone \"nv-cb-pop--phone\"}}\'\n          role={{if this.cbPhone \'dialog\'}}\n          aria-modal={{if this.cbPhone \'true\'}}\n          aria-label={{if this.cbPhone this.labelSearchInput}}\n          {{did-insert this.cbPlace}}\n          {{did-update this.cbReposition this.cbRows.length this.cbShowFilter this.searchError}}\n          {{on \'mousedown\' this.preventInputBlur}}\n        >\n          {{#if this.cbPhone}}\n            <div class=\'nv-cb-head\'>\n              <div class=\'nv-cb nv-cb--sheet\'>\n                <FaIcon @icon=\'magnifying-glass\' class=\'nv-cb-ic\' aria-hidden=\'true\' />\n                <input\n                  type=\'search\'\n                  class=\'nv-cb-input\'\n                  role=\'combobox\'\n                  autocomplete=\'off\'\n                  aria-autocomplete=\'list\'\n                  aria-expanded=\'true\'\n                  aria-controls={{this.cbListId}}\n                  aria-activedescendant={{this.cbActiveId}}\n                  aria-label={{this.labelSearchInput}}\n                  placeholder={{this.labelSearchInput}}\n                  value={{this.searchValue}}\n                  data-test-nv-cb-input\n                  {{on \'input\' this.cbInput}}\n                  {{on \'keydown\' this.cbKeydown}}\n                  {{did-insert this.focusSearchInput}}\n                />\n              </div>\n              <button type=\'button\' class=\'nv-cb-done\' data-test-nv-cb-done {{on \'click\' this.cbClose}}>\n                {{t \'nv.assoc.done\'}}\n              </button>\n            </div>\n          {{/if}}\n\n          {{#if this.cbStates}}\n            <div class=\'nv-cb-bar\'>\n              <span class=\'nv-cb-count\'>\n                {{#if this.cbTotal}}{{t \'search_results\' count=this.cbTotal}}{{/if}}\n              </span>\n              <button\n                type=\'button\'\n                class=\'nv-cb-filter {{if this.statusSelected \"is-on\"}}\'\n                aria-expanded={{if this.cbShowFilter \'true\' \'false\'}}\n                data-test-nv-cb-filter\n                {{on \'click\' this.cbToggleFilter}}\n              >\n                <FaIcon @icon=\'filter\' aria-hidden=\'true\' />\n                {{if this.statusSelected this.statusSelected.status_name (t \'nv.assoc.filter_state\')}}\n              </button>\n            </div>\n            {{#if this.cbShowFilter}}\n              <div class=\'nv-cb-states\' role=\'group\' aria-label={{t \'nv.assoc.filter_state\'}}>\n                <button\n                  type=\'button\'\n                  class=\'nv-cb-state {{unless this.statusSelected \"is-on\"}}\'\n                  aria-pressed={{if this.statusSelected \'false\' \'true\'}}\n                  {{on \'click\' (fn this.cbSelectStatus null)}}\n                >\n                  {{t \'nv.assoc.all_states\'}}\n                </button>\n                {{#each this.cbStates as |st|}}\n                  <button\n                    type=\'button\'\n                    class=\'nv-cb-state {{if st.on \"is-on\"}}\'\n                    aria-pressed={{if st.on \'true\' \'false\'}}\n                    {{on \'click\' (fn this.cbSelectStatus st.status)}}\n                  >\n                    {{st.label}}\n                  </button>\n                {{/each}}\n              </div>\n            {{/if}}\n          {{/if}}\n\n          <div class=\'nv-cb-scroll\'>\n            <ul\n              id={{this.cbListId}}\n              class=\'nv-cb-list\'\n              role=\'listbox\'\n              aria-label={{this.labelSearchInput}}\n              aria-multiselectable={{if @multiple \'true\'}}\n            >\n              {{#each this.cbOptions as |o|}}\n                <li\n                  id={{o.domId}}\n                  class=\'nv-cb-opt {{if o.focused \"is-focused\"}} {{if o.selected \"is-selected\"}}\'\n                  role=\'option\'\n                  aria-selected={{if o.selected \'true\' \'false\'}}\n                  aria-busy={{if o.pending \'true\'}}\n                    data-test-nv-cb-option\n                  {{on \'click\' (fn this.cbPick o.item)}}\n                >\n                  {{#if this.showThumbnail}}\n                    <span class=\'nv-cb-opt-th\'>\n                      <BbThumbnail\n                        @size={{28}}\n                        @item={{o.item}}\n                        @thumbnail_size=\'small\'\n                        @applicationController={{@applicationController}}\n                      />\n                    </span>\n                  {{/if}}\n                  <span class=\'nv-cb-opt-t\'>\n                    <span class=\'nv-cb-opt-l\'>\n                    {{~#if o.color~}}\n                      <span class=\'nv-dot\' style={{css-style \'background-color\' o.color}} aria-hidden=\'true\'></span>\n                    {{~/if~}}\n                    {{o.item.label}}\n                  </span>\n                    {{#if o.sub}}\n                      <span class=\'nv-cb-opt-s\'>{{o.sub}}</span>\n                    {{/if}}\n                  </span>\n                  {{#if o.pending}}\n                    <span class=\'nv-cb-pend\' data-test-nv-cb-pending><BbSpinner /></span>\n                  {{else if o.selected}}\n                    <FaIcon @icon=\'check\' class=\'nv-cb-check\' aria-hidden=\'true\' />\n                  {{/if}}\n                </li>\n              {{/each}}\n            </ul>\n\n            {{#if this.cbFirstLoad}}\n              <div class=\'nv-cb-msg\' data-test-nv-cb-loading><BbSpinner /></div>\n            {{else if this.searchError}}\n              <div class=\'nv-cb-msg\' data-test-nv-cb-error>\n                {{t \'nv.assoc.error\'}}\n                <button type=\'button\' class=\'nv-cb-link\' {{on \'click\' this.cbRetry}}>{{t \'nv.assoc.retry\'}}</button>\n              </div>\n            {{else if this.cbIsEmpty}}\n              <div class=\'nv-cb-msg\' data-test-nv-cb-empty>\n                {{#if this.searchValue}}\n                  {{t \'nv.assoc.no_match\' value=this.searchValue}}\n                {{else}}\n                  {{t \'nv.assoc.none\'}}\n                {{/if}}\n              </div>\n            {{/if}}\n\n            {{#if this.cbHasMore}}\n              <button type=\'button\' class=\'nv-cb-more\' data-test-nv-cb-more {{on \'click\' this.cbMore}}>\n                {{t \'nv.assoc.more\' count=this.cbMoreCount}}\n              </button>\n            {{/if}}\n          </div>\n\n          {{#if this.cbShowCreate}}\n            <button\n              type=\'button\'\n              class=\'nv-cb-create\'\n              disabled={{this.cbCreateTask.isRunning}}\n              data-test-nv-cb-create\n              {{on \'click\' (perform this.cbCreateTask)}}\n            >\n              <FaIcon @icon=\'plus\' aria-hidden=\'true\' />\n              <span>{{t \'nv.assoc.create\' value=this.searchValue}}</span>\n            </button>\n          {{/if}}\n        </div>\n      </div>\n    {{/in-element}}\n  {{/if}}\n  {{!-- A click / Enter on a ticked row asks before unlinking: the same confirm as the chip ✕ and the record preview\n       «scollega». Above the list (1160 > 1150); the list stays open, aria-hidden, under it. --}}\n  {{#if @multiple}}\n    <BbUnlinkConfirm\n      @isOpen={{this.cbConfirmOpen}}\n      @label={{this.cbConfirmItem.label}}\n      @onConfirm={{this.cbConfirmUnlink}}\n      @onClose={{this.cbConfirmClose}}\n      @zIndex=\'1160\'\n      data-test-nv-cb-unlink-confirm\n    />\n  {{/if}}\n{{else}}\n  <BasicDropdown\n    @matchTriggerWidth={{true}}\n    @verticalPosition={{this._verticalPosition}}\n    @destinationElement={{this.destinationElement}}\n    @onOpen={{this.onDropdownOpen}}\n    @onClose={{this.onDropdownClose}}\n    @registerAPI={{this.registerDropdownAPI}}\n    data-test-on-dropdown-open\n    data-test-on-dropdown-close\n    as |dd|\n  >\n    {{! ...attributes lands on the trigger, the control a user actually clicks to\n        start an association. Without it, every attribute a host passes to\n        <SelectAssociation> — including the `data-test-*` hook that names its\n        @action site — reached no element at all, because this template\'s root is\n        the <BasicDropdown> component and nothing else splatted. }}\n    <dd.Trigger\n      class=\'tail-select no-classes multiple {{if dd.isOpen \"active\"}}\'\n      ...attributes\n      {{did-insert this.onInsert}}\n    >\n      <div class=\'select-label\'>\n        <span class=\'label-inner\'>\n          {{#if this.searchItem.isRunning}}\n            <BbSpinner />\n          {{else}}\n            {{this.labelSearchInput}}\n          {{/if}}\n        </span>\n      </div>\n    </dd.Trigger>\n\n    <dd.Content class=\'tail-select active tail-select-portal\' data-nv-layer>\n      <div class=\'select-dropdown\'>\n        <div\n          class=\'dropdown-box\'\n          data-test-prevent-input-blur\n          {{on \'mousedown\' this.preventInputBlur}}\n        >\n          <form\n            class=\'dropdown-search search-reset\'\n            data-test-handle-submit\n            {{on \'submit\' this.handleSubmit}}\n          >\n            <div class=\'input-search\'>\n              <Input\n                @type=\'search\'\n                @value={{this.searchValue}}\n                placeholder=\'{{this.labelSearchInput}}\'\n                class=\'search-input\'\n                data-test-search-input\n                {{on \'input\' (perform this.searchInputTask)}}\n                {{did-insert this.focusSearchInput}}\n              />\n            </div>\n            <button class=\'reset-icon\' type=\'button\' data-test-close {{on \'click\' this.close}}>\n              <FaIcon @icon=\'times\' />\n            </button>\n          </form>\n\n          <div class=\'dropdown-inner\'>\n            <div class=\'grid-x dropdown-header\'>\n              <div class=\'cell shrink\'>\n                <span class=\'result\'>\n                  {{#if this.items}}\n                    {{t \'search_results\' count=this.items.meta.total_items}}\n                  {{/if}}\n                </span>\n              </div>\n              <SelectAssociation::FilterBar\n                @stateToLoop={{this.stateToLoop}}\n                @isOpen={{this.filter}}\n                @statusSelected={{this.statusSelected}}\n                @onToggleFilter={{this.toggleFilter}}\n                @onSelectStatus={{this.selectStatus}}\n                data-test-toggle-filter\n                data-test-select-status\n              />\n            </div>\n\n            <SelectAssociation::ResultList\n              @isLoading={{this.searchItem.isRunning}}\n              @items={{this.items}}\n              @focusIndex={{this.focusIndex}}\n              @pageLists={{this.pageLists}}\n              @searchValue={{this.searchValue}}\n              @less3Char={{this.less3Char}}\n              @searchError={{this.searchError}}\n              @showThumbnail={{this.showThumbnail}}\n              @applicationController={{@applicationController}}\n              @onSetItem={{this.setItem}}\n              @onSetPage={{this.setPage}}\n              @onShowAll={{this.showAll}}\n              data-test-set-item\n              data-test-set-page\n              data-test-show-all\n            />\n\n            {{#if this.searchValue}}\n              {{#if this.showAddButton}}\n                <button\n                  type=\'button\'\n                  class=\'button expanded flat add-new clearfix\'\n                  title=\'{{t \"add\"}} {{this.searchValue}}\'\n                  data-test-add-new-item\n                  {{on \'click\' (perform this.addNewItemTask)}}\n                >\n                  <FaIcon @icon=\'plus\' />\n                  {{t \'add\'}} \"{{this.searchValue}}\"\n                </button>\n              {{/if}}\n            {{/if}}\n          </div>\n        </div>\n      </div>\n    </dd.Content>\n  </BasicDropdown>\n\n  {{on-key \'ArrowDown\' this.downKey firstResponder=true}}\n  {{on-key \'ArrowUp\' this.upKey firstResponder=true}}\n  {{on-key \'ArrowLeft\' this.leftKey firstResponder=true}}\n  {{on-key \'ArrowRight\' this.rightKey firstResponder=true}}\n  {{on-key \'Enter\' this.enterKey firstResponder=true}}\n  {{!-- Escape belongs to the open pane only: a higher priority than the record / modal under it and inactive while\n       closed, so it neither closes the record behind an open pane nor answers for every closed field of the page. --}}\n  {{on-key \'Escape\' this.escKey priority=10 activated=this.isOpen}}\n{{/if}}\n");

var _dec, _dec2, _dec3, _dec4, _dec5, _dec6, _dec7, _dec8, _class, _descriptor, _descriptor2, _descriptor3, _descriptor4, _descriptor5, _descriptor6, _descriptor7, _descriptor8, _descriptor9, _descriptor0, _descriptor1, _descriptor10, _descriptor11, _descriptor12, _descriptor13, _descriptor14, _descriptor15, _descriptor16, _descriptor17, _descriptor18, _descriptor19, _descriptor20;
const DEBOUNCE_MS = 500;
// UI nuova combobox: search from the first character, 8 rows a page, a shorter debounce
const NV_DEBOUNCE_MS = 250;
const NV_PAGE_SIZE = 8;
const NV_POP_MIN_WIDTH = 280;

// popper modifier: the list is as wide as the field (never narrower than NV_POP_MIN_WIDTH)
const NV_SAME_WIDTH = {
  name: 'nvSameWidth',
  enabled: true,
  phase: 'beforeWrite',
  requires: ['computeStyles'],
  fn({
    state
  }) {
    state.styles.popper.width = `${Math.max(state.rects.reference.width, NV_POP_MIN_WIDTH)}px`;
  },
  effect({
    state
  }) {
    state.elements.popper.style.width = `${Math.max(state.elements.reference.offsetWidth, NV_POP_MIN_WIDTH)}px`;
  }
};

// Second line of a combobox row. A contact without tax code / VAT number had none, so six «BIANCHI MARIO» looked
// identical: fall back to what tells two people apart at the desk — birth date, mobile, email.
function cbOptionSub(item) {
  const sub = String(item?.sub_label ?? '').trim();
  if (sub || item?.constructor?.modelName !== 'contact') return sub;
  const [y, m, d] = String(item.data_nascita ?? '').slice(0, 10).split('-');
  const born = y && m && d ? `${d}/${m}/${y}` : '';
  return [born, item.mobile || item.telephone, item.mail].filter(Boolean).join(' · ');
}
let SelectAssociationComponent = (_dec = task({
  restartable: true
}), _dec2 = task({
  drop: true
}), _dec3 = task({
  restartable: true
}), _dec4 = task({
  restartable: true
}), _dec5 = task({
  restartable: true
}), _dec6 = task({
  restartable: true
}), _dec7 = task({
  enqueue: true
}), _dec8 = task({
  drop: true
}), _class = class SelectAssociationComponent extends Component {
  constructor(owner, args) {
    super(owner, args);
    _initializerDefineProperty(this, "session", _descriptor, this);
    _initializerDefineProperty(this, "intl", _descriptor2, this);
    _initializerDefineProperty(this, "store", _descriptor3, this);
    // --- Tracked state ---
    _initializerDefineProperty(this, "searchValue", _descriptor4, this);
    _initializerDefineProperty(this, "items", _descriptor5, this);
    _initializerDefineProperty(this, "itemSelected", _descriptor6, this);
    _initializerDefineProperty(this, "searchError", _descriptor7, this);
    _initializerDefineProperty(this, "pageLists", _descriptor8, this);
    _initializerDefineProperty(this, "page", _descriptor9, this);
    _initializerDefineProperty(this, "focusIndex", _descriptor0, this);
    _initializerDefineProperty(this, "statusSelected", _descriptor1, this);
    _initializerDefineProperty(this, "filter", _descriptor10, this);
    _initializerDefineProperty(this, "stopClosing", _descriptor11, this);
    _initializerDefineProperty(this, "ddAPI", _descriptor12, this);
    _initializerDefineProperty(this, "_verticalPosition", _descriptor13, this);
    // UI nuova combobox state (see the «UI nuova combobox» section)
    _initializerDefineProperty(this, "cbOpen", _descriptor14, this);
    _initializerDefineProperty(this, "cbPhone", _descriptor15, this);
    _initializerDefineProperty(this, "cbRows", _descriptor16, this);
    _initializerDefineProperty(this, "cbTotal", _descriptor17, this);
    _initializerDefineProperty(this, "cbShowFilter", _descriptor18, this);
    _initializerDefineProperty(this, "cbPendingId", _descriptor19, this);
    // the ticked row whose unlink waits for the shared Sì / No confirm (BbUnlinkConfirm)
    _initializerDefineProperty(this, "cbConfirmItem", _descriptor20, this);
    this.cbListId = `nv-cb-${guidFor(this)}`;
    this._cbRoot = null;
    this._cbPopper = null;
    if (args.searchValue !== undefined) {
      this.searchValue = args.searchValue;
    }
    const id = 'select-association-wormhole';
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement('div');
      el.id = id;
      document.body.appendChild(el);
    }
    this._destinationElement = el;
  }

  // --- Getters ---

  get destinationElement() {
    return this._destinationElement;
  }
  get isOpen() {
    return this.ddAPI?.isOpen ?? false;
  }
  get labelSearchInput() {
    return this.args.wrapLabelSearchInput || this.intl.t('search...');
  }
  get showThumbnail() {
    return this.args.showThumbnail ?? true;
  }
  get showRelated() {
    return this.args.showRelated ?? true;
  }
  get addNew() {
    return this.args.addNew ?? true;
  }
  get stateToLoop() {
    const organization = this.session.me?.organization;
    return organization?.get?.(`${this.args.relatedModelType}_states`) ?? null;
  }
  get less3Char() {
    // searchValue can be null (onDropdownOpen/_setItemTask reset) — the old
    // `!== undefined` guard let null through into `.length` (TypeError).
    const sv = this.searchValue || '';
    return sv.length > 0 && sv.length < 3;
  }
  get showAddButton() {
    const isClient = this.session.me?.role?.is_client;
    const isContactModel = this.args.relatedModelType === 'contact';
    if (isContactModel) {
      return !isClient && this.addNew;
    }
    return this.addNew;
  }

  // --- Setup ---

  onInsert() {
    this.items = [];
    this.statusSelected = this.args.status || null;
    this.pageLists = null;
    this.focusIndex = -1;
  }

  // --- BasicDropdown integration ---

  registerDropdownAPI(api) {
    this.ddAPI = api;
  }
  onDropdownOpen() {
    // Cancel any in-flight search: a run still pending from before the
    // dropdown closed would repopulate items after this reset.
    this.searchItem.cancelAll();
    this.items = [];
    this.statusSelected = this.args.status || null;
    this.pageLists = null;
    this.searchValue = null;
    this.page = 1;
    this.searchError = false;
    this.focusIndex = -1;

    // Flip: if not enough space below the trigger, open above.
    // Use visualViewport when available (accounts for virtual keyboard on mobile).
    const trigger = this.ddAPI?.actions?.getTriggerElement?.();
    if (trigger) {
      const MIN_DROP_HEIGHT = 220;
      const triggerRect = trigger.getBoundingClientRect();
      const viewportHeight = window.visualViewport?.height ?? window.innerHeight;
      const spaceBelow = viewportHeight - triggerRect.bottom;
      this._verticalPosition = spaceBelow < MIN_DROP_HEIGHT ? 'above' : 'below';
    }

    // Force reposition after CSS is applied to avoid initial misplacement.
    next(this, () => {
      if (!this.isDestroying && !this.isDestroyed) {
        this.ddAPI?.actions?.reposition?.();
      }
    });

    // On mobile, the virtual keyboard reduces the viewport after the input is
    // focused. Listen to visualViewport resize to reposition the dropdown.
    this._startVisualViewportListener();
  }
  onDropdownClose() {
    this._stopVisualViewportListener();
  }
  _startVisualViewportListener() {
    if (!window.visualViewport || this._vpResizeHandler) return;
    this._vpResizeHandler = () => {
      if (!this.isDestroying && !this.isDestroyed) {
        this.ddAPI?.actions?.reposition?.();
      }
    };
    window.visualViewport.addEventListener('resize', this._vpResizeHandler);
  }
  _stopVisualViewportListener() {
    if (!window.visualViewport || !this._vpResizeHandler) return;
    window.visualViewport.removeEventListener('resize', this._vpResizeHandler);
    this._vpResizeHandler = null;
  }
  focusSearchInput(element) {
    next(this, () => {
      if (!this.isDestroying && !this.isDestroyed) {
        // iOS Safari may ignore preventScroll — compensate by restoring scroll.
        const scrollY = window.scrollY;
        element.focus({
          preventScroll: true
        });
        requestAnimationFrame(() => {
          if (Math.abs(window.scrollY - scrollY) > 10) {
            window.scrollTo({
              top: scrollY,
              behavior: 'instant'
            });
          }
        });
      }
    });
  }
  willDestroy() {
    super.willDestroy(...arguments);
    this._stopVisualViewportListener();
    this._cbPopper?.destroy();
    this._cbPopper = null;
  }

  // --- Search & Data Tasks ---

  *searchItem(showAll = false, append = false) {
    const nuova = this.isNuova;
    let q = this.searchValue ?? '';
    if (this.args.appendQuery) {
      q = q ? `${q} ${this.args.appendQuery}` : this.args.appendQuery;
    }
    if (showAll) {
      this.page = 1;
    }

    // classic waits for 3 characters; the nuova combobox searches from the first one
    if (!nuova && !showAll && q.length > 0 && q.length < 3 && isNaN(q)) {
      this.items = [];
      this.pageLists = null;
      return;
    }
    yield timeout(nuova ? NV_DEBOUNCE_MS : DEBOUNCE_MS);
    const paramsToQuery = {
      q,
      xpag: nuova ? NV_PAGE_SIZE : 5,
      status: this.statusSelected?.id,
      pag: this.page,
      document_type: this.args.documentType,
      id: this.args.custom_field?.organization_custom_field?.id,
      contact_id: this.args.filter_by_contact,
      product_id: this.args.filter_by_product,
      project_id: this.args.filter_by_project,
      role: this.args.role?.id
    };
    this.pageLists = null;
    this.searchError = false;
    try {
      const items = yield this.store.query(this.args.relatedModelType, paramsToQuery);
      this.items = items;
      if (items?.meta?.total_pages) {
        this.pageLists = paginatorPageLists(items.meta.total_pages, this.page);
      }
      if (nuova) {
        // «Mostra altri» appends the next page under the rows already shown
        const rows = Array.from(items ?? []);
        this.cbRows = append ? [...this.cbRows, ...rows] : rows;
        this.cbTotal = Number(items?.meta?.total_items) || this.cbRows.length;
      }
    } catch {
      this.items = [];
      this.searchError = true;
      if (nuova && !append) {
        this.cbRows = [];
        this.cbTotal = 0;
      }
    }
  }
  *addNewItemTask() {
    const relatedModelType = this.args.relatedModelType;
    const modelStatesName = `${relatedModelType}_states`;
    const statusName = `status_${this.args.defaultStatusName ? 'name' : 'default'}`;
    const statusValue = this.args.defaultStatusName || true;
    const orgModelState = relatedModelType ? this.session.me?.organization?.[modelStatesName] : null;
    const orgDefaultState = orgModelState ? orgModelState.filter(item => item[statusName] === statusValue)?.[0] : null;

    // A panel scoped to a status (`@status` — the room / electromedical /
    // specialists panels on product-detail, one panel per contact state in
    // related-contacts) must create the record IN THAT STATUS. Seeding it from
    // the organization's `status_default` instead made has-many's own status
    // filter (has-many.js:106) drop the record that had just been created and
    // linked: no chip appeared, the operator read it as a failed add and created
    // the record again — duplicates, each of them really linked to the host.
    const defaultState = this.args.status ?? orgDefaultState;
    let newRecord;
    try {
      newRecord = yield this.args.applicationController.newRecordFactory.createNewModel.perform(relatedModelType, null, defaultState, false);
    } catch {
      return;
    }
    newRecord.role = this.args.role;
    if (relatedModelType === 'contact') {
      // Operators type COGNOME then NOME (surname first): the last token is the
      // first name, everything before it is the surname. A single token is
      // treated as the surname (last_name), matching the surname-first order.
      const fullName = this.searchValue || '';
      const names = fullName.trim().split(/\s+/);
      const nome = names.pop();
      const cognome = names.join(' ');
      newRecord.setProperties({
        first_name: cognome ? nome : '',
        last_name: cognome || nome
      });
    }
    let itemAPI;
    try {
      itemAPI = yield this.args.applicationController.saveRecordRelated(newRecord);
    } catch {
      return;
    }
    if (this.isDestroying || this.isDestroyed) return;

    // saveRecordRelated resolves false on failure: no id, nothing to peek
    // (peekRecord with undefined id asserts).
    const newId = itemAPI?.[0]?.data?.id;
    const itemStore = newId ? this.store.peekRecord(relatedModelType, newId) : null;
    if (itemStore) {
      this.ddAPI?.actions.close();
      if (typeof this.args.action === 'function') {
        this.args.action(itemStore.id);
      }
      if (defaultState) {
        const modelName = itemStore.constructor.modelName;
        const obj = {
          title: this._createdTitle(modelName),
          componentName: this.session.me.get(`role.application.${modelName}_edit`),
          item: itemStore
        };
        if (this.args.updateModelAfterSave) {
          obj.updateModelAfterSave = {
            action: this.args.updateModelAfterSave,
            item: this.args.modelToUpdate
          };
        }
        const appController = this.args.applicationController;
        if (appController) {
          set(appController, 'modalComponents', [...(appController.modalComponents || []), obj]);
        }
      }
    }
  }
  *closeDropDownTask() {
    yield timeout(1000);
    if (this.isDestroying || this.isDestroyed) return;
    if (this.stopClosing) {
      this.stopClosing = false;
    } else {
      this.ddAPI?.actions.close();
    }
  }
  *_setItemTask(item) {
    yield timeout(50);
    if (this.isDestroying || this.isDestroyed) return;
    this.searchValue = this.showRelated ? null : item.label;
    this.ddAPI?.actions.close();
    this.itemSelected = item;
    if (typeof this.args.action === 'function') {
      this.args.action(item.id);
    }
  }
  *searchInputTask() {
    yield timeout(50);
    if (this.isDestroying || this.isDestroyed) return;
    if (this.searchValue && this.searchValue !== '') {
      this.page = 1;
      this.searchItem.perform();
    } else {
      // Also cancel the debounced search still holding the previous query,
      // or it would resume after its timeout and overwrite the cleared list.
      this.searchItem.cancelAll();
      this.items = [];
      this.pageLists = null;
    }
  }

  // --- Actions ---

  handleSubmit(event) {
    event.preventDefault();
  }
  close() {
    this.ddAPI?.actions.close();
  }
  noClose() {
    this.stopClosing = true;
  }

  /**
   * Prevents the input from losing focus when the user clicks
   * anywhere inside the dropdown (results, buttons, pagination, etc.).
   */
  preventInputBlur(event) {
    if (event.target.tagName !== 'INPUT') {
      event.preventDefault();
    }
  }

  /**
   * "Show all results" — a named action rather than an inline
   * `{{fn (perform this.searchItem) true}}` in the template, so the site carries
   * its own `data-test-show-all` hook and can be driven (and credited) on its own
   * instead of sharing the invocation's first hook with @onSetItem/@onSetPage.
   */
  showAll() {
    this.searchItem.perform(true);
  }
  setPage(page) {
    this.page = page;
    this.searchItem.perform();
  }
  toggleFilter() {
    this.filter = !this.filter;
  }
  selectStatus(item) {
    this.statusSelected = item === this.statusSelected ? null : item;
    this.page = 1;
    this.focusIndex = -1;
    this.searchItem.perform();
  }
  setItem(item) {
    this._setItemTask.perform(item);
  }

  // --- Keyboard Navigation ---

  upKey() {
    if (!this.isOpen) return;
    this.focusItemAt(this.focusIndex - 1);
  }
  downKey() {
    if (!this.isOpen) return;
    this.focusItemAt(this.focusIndex + 1);
  }
  escKey(_event, ekEvent) {
    if (!this.isOpen) return;
    this.ddAPI.actions.close();
    // the pane took this Escape: the record / modal listening at a lower priority must not close too
    ekEvent?.stopPropagation?.();
  }
  leftKey() {
    if (!this.isOpen) return;
    let page = this.page - 1;
    if (page <= 0) page = 1;
    this.page = page;
    this.focusIndex = -1;
    this.searchItem.perform();
  }
  rightKey() {
    if (!this.isOpen) return;
    const nextPage = this.page + 1;
    if (this.items?.meta?.total_pages && this.items.meta.total_pages >= nextPage) {
      this.page = nextPage;
      this.focusIndex = -1;
      this.searchItem.perform();
    }
  }
  enterKey() {
    if (!this.isOpen || !this.items?.length) return;
    const itemSelected = this.items.objectAt ? this.items.objectAt(this.focusIndex) : this.items[this.focusIndex];
    if (itemSelected) {
      this._setItemTask.perform(itemSelected);
    }
  }
  focusItemAt(index) {
    if (!this.isOpen) return;
    const len = this.items?.length ?? 0;
    if (len === 0) return;
    if (index < 0) {
      index = len - 1;
    } else if (index >= len) {
      index = 0;
    }
    this.focusIndex = index;
  }

  // --- UI nuova combobox ---
  // The field IS the search input (role=combobox): focus or click opens the list under it with the first page
  // already loaded, typing filters from the first character, «Mostra altri» appends the next page. Multi-pick
  // hosts (HasMany: @multiple, @selectedIds, @onRemove) keep the list open, tick what is already chosen and untick
  // it on a second click. Phone (< 40rem, checked on open): a full-screen layer with its own input and
  // «Fatto» (the page field stays an input; the layer input takes the focus). Classic keeps the BasicDropdown branch
  // above, untouched.

  // looked up, not injected: an app (or a test) without service:ui keeps the classic branch instead of throwing
  get isNuova() {
    return getOwner(this)?.lookup('service:ui')?.isNuova === true;
  }

  // read when the list opens (matchMedia, fresh each call): phone = full-screen layer with its own input
  get cbIsPhone() {
    return getOwner(this)?.lookup('service:window-viewport')?.isSmallDevice === true;
  }
  get cbSelectedIds() {
    return new Set((this.args.selectedIds ?? []).map(id => String(id)));
  }
  get cbOptions() {
    const selected = this.cbSelectedIds;
    return this.cbRows.filter(item => item?.id).map((item, index) => ({
      item,
      index,
      domId: `${this.cbListId}-${index}`,
      selected: selected.has(String(item.id)),
      pending: String(item.id) === this.cbPendingId,
      focused: index === this.focusIndex,
      color: item.contact_state?.status_color,
      sub: cbOptionSub(item)
    }));
  }
  get cbActiveId() {
    return this.focusIndex >= 0 ? `${this.cbListId}-${this.focusIndex}` : null;
  }
  get cbHasMore() {
    return this.cbRows.length < this.cbTotal;
  }
  get cbMoreCount() {
    return this.cbTotal - this.cbRows.length;
  }
  get cbShowCreate() {
    return Boolean(this.searchValue) && this.showAddButton;
  }
  get cbFirstLoad() {
    return this.searchItem.isRunning && this.cbRows.length === 0;
  }
  get cbIsEmpty() {
    return !this.searchItem.isRunning && !this.searchError && this.cbRows.length === 0;
  }
  get cbStates() {
    const states = this.stateToLoop;
    if (!states || (states.length ?? 0) < 2) return null;
    const current = this.statusSelected?.id;
    return Array.from(states).map(status => ({
      status,
      label: status.status_name,
      on: current != null && String(status.id) === String(current)
    }));
  }
  cbRegisterRoot(element) {
    this._cbRoot = element;
  }
  cbOpenList() {
    // silent-guard-ok: the list is already open
    if (this.cbOpen) return;
    this.cbPhone = this.cbIsPhone;
    this.cbOpen = true;
    this.focusIndex = -1;
    this.searchError = false;
    this.statusSelected = this.args.status || null;
    this.page = 1;
    // the list is never empty on open: the first page, filtered by what is already typed
    this.searchItem.perform(true);
  }
  cbClose() {
    this.searchItem.cancelAll();
    this.cbBlurTask.cancelAll();
    this._cbPopper?.destroy();
    this._cbPopper = null;
    this.cbOpen = false;
    this.cbShowFilter = false;
    this.cbRows = [];
    this.cbTotal = 0;
    this.focusIndex = -1;
    this.page = 1;
    // a single pick with @showRelated=false shows the chosen label in the field
    this.searchValue = this.showRelated ? '' : this.itemSelected?.label ?? '';
  }
  cbInput(event) {
    this.searchValue = event.target.value;
    this.page = 1;
    this.focusIndex = -1;
    if (!this.cbOpen) {
      this.cbOpenList();
      return;
    }
    this.searchItem.perform();
  }
  cbKeydown(event) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!this.cbOpen) {
          this.cbOpenList();
        } else {
          this._cbMove(1);
        }
        break;
      case 'ArrowUp':
        event.preventDefault();
        this._cbMove(-1);
        break;
      case 'Enter':
        {
          event.preventDefault();
          const option = this.cbOptions[this.focusIndex];
          if (option) this.cbPick(option.item);
          break;
        }
      case 'Escape':
        if (this.cbOpen) {
          // the list owns this Escape: the record / modal under it must not receive it
          event.preventDefault();
          event.stopPropagation();
          this.cbClose();
        }
        break;
      case 'Tab':
        if (this.cbOpen && !this.cbPhone) this.cbClose();
        break;
    }
  }
  _cbMove(step) {
    const len = this.cbOptions.length;
    if (!this.cbOpen || len === 0) return;
    let index = this.focusIndex + step;
    if (index < 0) index = len - 1;
    if (index >= len) index = 0;
    this.focusIndex = index;
    document.getElementById(`${this.cbListId}-${index}`)?.scrollIntoView?.({
      block: 'nearest'
    });
  }
  cbBlur() {
    this.cbBlurTask.perform();
  }

  // focus left the field: close unless it moved into the list (a click there keeps the input focused anyway)
  *cbBlurTask() {
    yield timeout(150);
    if (this.isDestroying || this.isDestroyed || !this.cbOpen || this.cbPhone) return;
    // the unlink confirm took the focus: the list stays open under it, the focus comes back to the field
    // silent-guard-ok: focus management while the unlink confirm is open
    if (this.cbConfirmItem) return;
    const active = document.activeElement;
    // silent-guard-ok: focus management, not a user refusal
    if (this._cbRoot?.contains(active)) return;
    // silent-guard-ok: focus management, not a user refusal
    if (active?.closest?.(`[data-nv-cb="${this.cbListId}"]`)) return;
    this.cbClose();
  }
  cbPick(item) {
    if (!item?.id) return;
    if (this.args.multiple === true) {
      // unlinking a ticked row always asks first (owner 2026-10-09, the same confirm as the chip ✕); linking an
      // unticked row stays immediate
      if (this.cbSelectedIds.has(String(item.id)) && typeof this.args.onRemove === 'function') {
        // silent-guard-ok: the unlink confirm on screen asks first
        if (this.cbConfirmItem) return;
        this.focusIndex = this.cbOptions.findIndex(o => String(o.item.id) === String(item.id));
        this.cbConfirmItem = item;
        return;
      }
      this.cbPickTask.perform(item);
      return;
    }
    this.itemSelected = item;
    if (typeof this.args.action === 'function') {
      this.args.action(item.id);
    }
    this.cbClose();
  }
  get cbConfirmOpen() {
    return this.cbConfirmItem !== null;
  }

  // «Sì»: exactly the unlink a second click ran before the confirm existed (cbPickTask's untick branch, row
  // spinner included). Returned so the confirm stays busy until the host's write settles.
  cbConfirmUnlink() {
    const item = this.cbConfirmItem;
    // unlinked meanwhile (e.g. from its chip ✕): never turn the answer into a link
    // silent-guard-ok: the item is already unlinked: nothing to confirm
    if (!item || !this.cbSelectedIds.has(String(item.id))) return null;
    return this.cbPickTask.perform(item);
  }

  // «No» / Escape / backdrop, or the unlink settled: the confirm closes, the list stays open and the focus goes
  // back to the field with the row still the active option (aria-activedescendant)
  cbConfirmClose() {
    const id = this.cbConfirmItem ? String(this.cbConfirmItem.id) : null;
    this.cbConfirmItem = null;
    next(this, () => {
      if (this.isDestroying || this.isDestroyed || !this.cbOpen) return;
      if (id) {
        const index = this.cbOptions.findIndex(o => String(o.item.id) === id);
        if (index >= 0) this.focusIndex = index;
      }
      const input = this.cbPhone ? document.querySelector(`[data-nv-cb="${this.cbListId}"] input`) : this._cbRoot?.querySelector('input');
      input?.focus({
        preventScroll: true
      });
    });
  }

  // Multi-pick: the host saves on every click (non-optimistic) and the tick appears only once the server confirmed
  // it — until then the clicked row shows a spinner, so the click is never «nothing happened». A second click on a
  // ticked row unticks it the same way, after the Sì / No confirm (cbConfirmUnlink). Enqueued: quick clicks on
  // several rows run one after the other.
  *cbPickTask(item) {
    const onAction = this.args.action;
    const onRemove = this.args.onRemove;
    const id = String(item.id);
    const untick = this.cbSelectedIds.has(id);
    this.cbPendingId = id;
    try {
      let result = null;
      if (untick) {
        if (typeof onRemove === 'function') result = onRemove(item.id);
      } else {
        this.itemSelected = item;
        if (typeof onAction === 'function') result = onAction(item.id);
      }
      if (result && typeof result.then === 'function') yield result;
    } finally {
      if (!this.isDestroying && !this.isDestroyed && this.cbPendingId === id) {
        this.cbPendingId = null;
      }
    }
  }
  cbMore() {
    this.page = this.page + 1;
    this.focusIndex = -1;
    this.searchItem.perform(false, true);
  }
  cbRetry() {
    this.searchError = false;
    this.page = 1;
    this.searchItem.perform();
  }
  cbToggleFilter() {
    this.cbShowFilter = !this.cbShowFilter;
  }
  cbSelectStatus(status) {
    this.statusSelected = status;
    this.page = 1;
    this.focusIndex = -1;
    this.searchItem.perform();
  }
  *cbCreateTask() {
    yield this.addNewItemTask.perform();
    if (this.isDestroying || this.isDestroyed) return;
    if (this.cbOpen) this.cbClose();
  }
  cbPlace(element) {
    if (this.cbPhone || !this._cbRoot) return;
    this._cbPopper?.destroy();
    this._cbPopper = createPopper(this._cbRoot, element, {
      strategy: 'fixed',
      placement: 'bottom-start',
      modifiers: [{
        name: 'offset',
        options: {
          offset: [0, 4]
        }
      }, {
        name: 'flip',
        options: {
          fallbackPlacements: ['top-start']
        }
      }, {
        name: 'preventOverflow',
        options: {
          padding: 8
        }
      }, NV_SAME_WIDTH]
    });
  }
  cbReposition() {
    this._cbPopper?.update();
  }

  /**
   * Title of the edit modal opened on a record just created from the search («Crea «Mario Rossi»»). It used to
   * be `t('edit') + modelName` — «Modifica contact», raw English. Now the organization's own name of the type
   * («Modifica Paziente»); in the «UI nuova» a contact reads as what it is: «Nuovo paziente».
   */
  _createdTitle(modelName) {
    const rName = modelName === 'invoice' ? this.args.documentType : modelName;
    const orgName = this.session.me?.organization?.get?.(`${rName}_name`);
    const noun = typeof orgName === 'string' && orgName.trim() ? orgName.trim() : null;
    if (modelName === 'contact' && this.isNuova) {
      const word = noun ? noun.toLowerCase() : this.intl.t('nv.contact.list.noun');
      return this.intl.t('nv.contact.list.new_label', {
        noun: word
      });
    }
    return `${this.intl.t('edit')} ${noun || rName}`;
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "session", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor2 = _applyDecoratedDescriptor(_class.prototype, "intl", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor3 = _applyDecoratedDescriptor(_class.prototype, "store", [inject], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _descriptor4 = _applyDecoratedDescriptor(_class.prototype, "searchValue", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return '';
  }
}), _descriptor5 = _applyDecoratedDescriptor(_class.prototype, "items", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor6 = _applyDecoratedDescriptor(_class.prototype, "itemSelected", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor7 = _applyDecoratedDescriptor(_class.prototype, "searchError", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor8 = _applyDecoratedDescriptor(_class.prototype, "pageLists", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor9 = _applyDecoratedDescriptor(_class.prototype, "page", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 1;
  }
}), _descriptor0 = _applyDecoratedDescriptor(_class.prototype, "focusIndex", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return -1;
  }
}), _descriptor1 = _applyDecoratedDescriptor(_class.prototype, "statusSelected", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor10 = _applyDecoratedDescriptor(_class.prototype, "filter", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor11 = _applyDecoratedDescriptor(_class.prototype, "stopClosing", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor12 = _applyDecoratedDescriptor(_class.prototype, "ddAPI", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor13 = _applyDecoratedDescriptor(_class.prototype, "_verticalPosition", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 'below';
  }
}), _descriptor14 = _applyDecoratedDescriptor(_class.prototype, "cbOpen", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor15 = _applyDecoratedDescriptor(_class.prototype, "cbPhone", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor16 = _applyDecoratedDescriptor(_class.prototype, "cbRows", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return [];
  }
}), _descriptor17 = _applyDecoratedDescriptor(_class.prototype, "cbTotal", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return 0;
  }
}), _descriptor18 = _applyDecoratedDescriptor(_class.prototype, "cbShowFilter", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return false;
  }
}), _descriptor19 = _applyDecoratedDescriptor(_class.prototype, "cbPendingId", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _descriptor20 = _applyDecoratedDescriptor(_class.prototype, "cbConfirmItem", [tracked], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: function () {
    return null;
  }
}), _applyDecoratedDescriptor(_class.prototype, "onInsert", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onInsert"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "registerDropdownAPI", [action], Object.getOwnPropertyDescriptor(_class.prototype, "registerDropdownAPI"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onDropdownOpen", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onDropdownOpen"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onDropdownClose", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onDropdownClose"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "focusSearchInput", [action], Object.getOwnPropertyDescriptor(_class.prototype, "focusSearchInput"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "searchItem", [_dec], Object.getOwnPropertyDescriptor(_class.prototype, "searchItem"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "addNewItemTask", [_dec2], Object.getOwnPropertyDescriptor(_class.prototype, "addNewItemTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "closeDropDownTask", [_dec3], Object.getOwnPropertyDescriptor(_class.prototype, "closeDropDownTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "_setItemTask", [_dec4], Object.getOwnPropertyDescriptor(_class.prototype, "_setItemTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "searchInputTask", [_dec5], Object.getOwnPropertyDescriptor(_class.prototype, "searchInputTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "handleSubmit", [action], Object.getOwnPropertyDescriptor(_class.prototype, "handleSubmit"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "close", [action], Object.getOwnPropertyDescriptor(_class.prototype, "close"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "noClose", [action], Object.getOwnPropertyDescriptor(_class.prototype, "noClose"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "preventInputBlur", [action], Object.getOwnPropertyDescriptor(_class.prototype, "preventInputBlur"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "showAll", [action], Object.getOwnPropertyDescriptor(_class.prototype, "showAll"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setPage", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setPage"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "toggleFilter", [action], Object.getOwnPropertyDescriptor(_class.prototype, "toggleFilter"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "selectStatus", [action], Object.getOwnPropertyDescriptor(_class.prototype, "selectStatus"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setItem", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setItem"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "upKey", [action], Object.getOwnPropertyDescriptor(_class.prototype, "upKey"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "downKey", [action], Object.getOwnPropertyDescriptor(_class.prototype, "downKey"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "escKey", [action], Object.getOwnPropertyDescriptor(_class.prototype, "escKey"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "leftKey", [action], Object.getOwnPropertyDescriptor(_class.prototype, "leftKey"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "rightKey", [action], Object.getOwnPropertyDescriptor(_class.prototype, "rightKey"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "enterKey", [action], Object.getOwnPropertyDescriptor(_class.prototype, "enterKey"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "cbRegisterRoot", [action], Object.getOwnPropertyDescriptor(_class.prototype, "cbRegisterRoot"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "cbOpenList", [action], Object.getOwnPropertyDescriptor(_class.prototype, "cbOpenList"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "cbClose", [action], Object.getOwnPropertyDescriptor(_class.prototype, "cbClose"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "cbInput", [action], Object.getOwnPropertyDescriptor(_class.prototype, "cbInput"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "cbKeydown", [action], Object.getOwnPropertyDescriptor(_class.prototype, "cbKeydown"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "cbBlur", [action], Object.getOwnPropertyDescriptor(_class.prototype, "cbBlur"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "cbBlurTask", [_dec6], Object.getOwnPropertyDescriptor(_class.prototype, "cbBlurTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "cbPick", [action], Object.getOwnPropertyDescriptor(_class.prototype, "cbPick"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "cbConfirmUnlink", [action], Object.getOwnPropertyDescriptor(_class.prototype, "cbConfirmUnlink"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "cbConfirmClose", [action], Object.getOwnPropertyDescriptor(_class.prototype, "cbConfirmClose"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "cbPickTask", [_dec7], Object.getOwnPropertyDescriptor(_class.prototype, "cbPickTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "cbMore", [action], Object.getOwnPropertyDescriptor(_class.prototype, "cbMore"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "cbRetry", [action], Object.getOwnPropertyDescriptor(_class.prototype, "cbRetry"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "cbToggleFilter", [action], Object.getOwnPropertyDescriptor(_class.prototype, "cbToggleFilter"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "cbSelectStatus", [action], Object.getOwnPropertyDescriptor(_class.prototype, "cbSelectStatus"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "cbCreateTask", [_dec8], Object.getOwnPropertyDescriptor(_class.prototype, "cbCreateTask"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "cbPlace", [action], Object.getOwnPropertyDescriptor(_class.prototype, "cbPlace"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "cbReposition", [action], Object.getOwnPropertyDescriptor(_class.prototype, "cbReposition"), _class.prototype), _class);
setComponentTemplate(TEMPLATE, SelectAssociationComponent);

export { cbOptionSub, SelectAssociationComponent as default };
//# sourceMappingURL=select-association.js.map
